import { storage } from '../lib/storage';

export interface SaveLegalHrDocumentOptions {
  userId: string;
  docType: string;
  blob: Blob;
  baseName?: string;
  effectiveDate?: string | null;
  metadata?: Record<string, unknown>;
  pathSuffix?: string;
  visibility?: 'private' | 'shared' | 'public';
  tags?: string[];
  createSignedUrl?: boolean;
  signedUrlTtl?: number;
}

export interface SavedLegalHrDocument {
  key: string;
  filename: string;
  version: number;
  signedUrl: string | null;
}

const sanitizeSegment = (segment: string): string =>
  segment
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'document';

const buildBaseName = ({ baseName, docType }: { baseName?: string; docType: string }) => {
  if (baseName && baseName.trim().length > 0) {
    return sanitizeSegment(baseName);
  }
  return sanitizeSegment(docType);
};

const buildDateStamp = (effectiveDate?: string | null) => {
  if (!effectiveDate) {
    const today = new Date();
    return `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
  }
  return effectiveDate.replace(/-/g, '');
};

const listExistingVersions = async (prefix: string) => {
  try {
    const { items } = await storage.listFiles({ prefix });
    return items
      .map((item) => item?.name || item?.objectKey || '')
      .map((name) => {
        const match = name.match(/_v(\d+)\.pdf$/i);
        return match ? parseInt(match[1], 10) : null;
      })
      .filter((value): value is number => typeof value === 'number' && !Number.isNaN(value));
  } catch (error) {
    console.warn('Failed to list existing Legal/HR documents', error);
    return [];
  }
};

const determineNextVersion = async (prefix: string) => {
  const versions = await listExistingVersions(prefix);
  if (!versions.length) {
    return 1;
  }
  return Math.max(...versions) + 1;
};

export const saveLegalHrDocument = async (options: SaveLegalHrDocumentOptions): Promise<SavedLegalHrDocument> => {
  const {
    userId,
    docType,
    blob,
    baseName,
    effectiveDate,
    metadata,
    pathSuffix,
    visibility = 'private',
    tags = [],
    createSignedUrl = true,
    signedUrlTtl = 3600,
  } = options;

  if (!userId) {
    throw new Error('Legal/HR storage requires an authenticated user');
  }

  const sanitizedDocType = sanitizeSegment(docType);
  const prefix = `legal-hr/${userId}/${sanitizedDocType}${pathSuffix ? `/${sanitizeSegment(pathSuffix)}` : ''}/`;
  const version = await determineNextVersion(prefix);
  const filename = `${buildBaseName({ baseName, docType })}_${buildDateStamp(effectiveDate)}_v${version}.pdf`;
  const path = `${prefix}${filename}`;

  const file = new File([blob], filename, { type: 'application/pdf' });
  const upload = await storage.uploadFile({
    file,
    path,
    metadata: {
      sourceModule: 'legal-hr',
      docType: sanitizedDocType,
      visibility,
      version,
      effectiveDate: effectiveDate ?? null,
      tags,
      ...metadata,
      savedAt: new Date().toISOString(),
    },
  });

  let signedUrl: string | null = null;
  if (createSignedUrl) {
    try {
      signedUrl = await storage.getSignedUrl(upload.key, signedUrlTtl);
    } catch (error) {
      console.warn('Failed to create signed URL for Legal/HR document', error);
    }
  }

  return {
    key: upload.key,
    filename,
    version,
    signedUrl,
  };
};

export const legalHrStorageService = {
  saveLegalHrDocument,
};

export default legalHrStorageService;
